import { Divider } from "@mui/material";
import { Fragment } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import type { Field, FieldIdToValueMap, PageLayout } from "../../../../api/types/objectTypes";
import type { InvoiceCardPreviewConfig } from "./invoiceCardPreviewState";
import InvoiceRelatedObjectsGroup from "./InvoiceRelatedObjectsGroup";
import type { RelatedObjectCellDecorations } from "./RelatedObjectsGrid";
import type { FieldValueLabeler } from "./relatedObjectHelper";
import type { RelatedObjectRowModel } from "./relatedObjectValuesState";
import type { InvoiceFieldValuesEditor } from "./useInvoiceFieldValuesEditor";

interface Props {
  editor: InvoiceFieldValuesEditor;
  cardLayout: PageLayout;
  allowEditing: boolean;
  invoiceFields: Field[];
  cardPreview?: InvoiceCardPreviewConfig;
  sourceRelatedObjectType?: string;
  previewFieldValues?: FieldIdToValueMap;
  cellDecorations?: RelatedObjectCellDecorations;
  noOptionsTexts?: Record<string, string>;
  hasPreviewFailed: boolean;
  onUserRowChange: (
    rowId: string,
    oldRow: RelatedObjectRowModel,
    updatedRow: RelatedObjectRowModel,
    labelOf: FieldValueLabeler
  ) => void;
}

const InvoiceRelatedObjects = ({
  editor,
  cardLayout,
  allowEditing,
  invoiceFields,
  cardPreview,
  sourceRelatedObjectType,
  previewFieldValues,
  cellDecorations,
  noOptionsTexts,
  hasPreviewFailed,
  onUserRowChange,
}: Props) => {
  const {
    relatedObjectValuesState,
    relatedObjectsResp,
    relatedObjectsError,
    isFetchingRelatedObjects,
    lookupOptionsOfRelatedObjectsByObjectType,
    relatedObjectFieldsByObjectType,
    validationErrorsByObjectType,
  } = editor;

  return (
    <>
      {relatedObjectsError && (
        <>
          <Divider sx={{ my: 3 }} />
          <DataLoadingFailed
            title="Failed to load related objects"
            text={cardLayout.relatedObjectGrids?.map((x) => x.name).join(", ") ?? ""}
          />
        </>
      )}

      {!relatedObjectsError && relatedObjectsResp === undefined && <InlineLoader />}

      {relatedObjectsResp !== undefined &&
        Object.entries(relatedObjectValuesState.rowsByObjectType).map(([relatedObjectType, rows]) => {
          const layout = cardLayout.relatedObjectGrids?.find((x) => x.objectType === relatedObjectType);
          if (!layout) {
            return null;
          }

          const relatedObjectsData = relatedObjectsResp.relatedObjectsByType.find(
            (x) => x.objectType === relatedObjectType
          );
          if (!relatedObjectsData) {
            return null;
          }

          return (
            <Fragment key={relatedObjectType}>
              <Divider sx={{ my: 2 }} />
              <InvoiceRelatedObjectsGroup
                layout={layout}
                fields={relatedObjectFieldsByObjectType[relatedObjectType] ?? []}
                objectPermissions={relatedObjectsData.objectPermissions}
                rows={rows}
                setState={editor.handleRelatedObjectValuesChange}
                allowEditing={allowEditing}
                allowAddingAndDeletingRows={relatedObjectType !== sourceRelatedObjectType}
                loading={isFetchingRelatedObjects}
                lookupOptions={lookupOptionsOfRelatedObjectsByObjectType?.[relatedObjectType] ?? {}}
                onLookupOptionsNeeded={editor.handleLookupOptionsNeeded}
                validationErrors={validationErrorsByObjectType[relatedObjectType] ?? {}}
                invoiceFields={invoiceFields}
                invoiceFieldValues={editor.fieldValuesState.fieldValues}
                cardPreview={cardPreview}
                previewFieldValues={previewFieldValues}
                cellDecorations={cellDecorations}
                noOptionsTexts={noOptionsTexts}
                hasPreviewFailed={hasPreviewFailed}
                onUserRowChange={onUserRowChange}
              />
            </Fragment>
          );
        })}
    </>
  );
};

export default InvoiceRelatedObjects;
